import React, { useState, useCallback } from 'react';
import { useDashboardData } from '../hooks/useDashboardData';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { storage } from '../../utils/platform';
import { openExternalAuthFlow } from '../utils/external';
import MobileHeader from '../components/MobileHeader';
import GoogleBusinessBanner from '../sections/GoogleBusinessBanner';
import KPIGrid from '../sections/KPIGrid';
import ObjectifSection from '../sections/ObjectifSection';
import ImpactBusinessSection from '../sections/ImpactBusinessSection';
import EvolutionNoteSection from '../sections/EvolutionNoteSection';
import { API_BASE } from '../../config/api';

// Bug 3 Vague 10 — clé localStorage du dismiss "Plus tard" sur le bandeau GMB.
// Préfixe revyo_* pour cleanup auto au logout (cf. utils/logout.js Session 5).
// TTL 7 jours : la bannière re-apparaît automatiquement après ce délai.
const GMB_BANNER_DISMISS_KEY = 'revyo_gmb_banner_dismissed_until';
const GMB_BANNER_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Déclenche l'OAuth GMB depuis le bandeau Accueil (Bug 1 Session 9).
// Aligné sur Profil Session 5 (useGmbStatus.startOAuth) : POST /api/gmb/auth/start
// retourne { url }, puis openExternalAuthFlow(url) — Safari View Controller en
// natif (préserve l'état React), top-level redirect en web (Google bloque les
// WebView in-app pour OAuth).
async function startGmbOAuth() {
  const token = await storage.get('token');
  if (!token) return;
  try {
    const res = await fetch(`${API_BASE}/api/gmb/auth/start`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      console.error('[MobileHome] gmb/auth/start failed:', res.status);
      return;
    }
    const data = await res.json();
    if (data?.url) await openExternalAuthFlow(data.url);
  } catch (e) {
    console.error('[MobileHome] gmb/auth/start error:', e.message);
  }
}

export default function MobileHome() {
  const {
    user,
    restaurant,
    stats,
    objectif_avis,
    avis_generes_revyo,
    progression_pilote,
    googleConnected,
    loading,
    error,
  } = useDashboardData();

  // Vague 10 multi-resto — ouverture du switcher au tap sur le nom resto du header.
  const { openSwitcher } = useCurrentRestaurant();

  // Bug 3 Vague 10 — bannière GMB "Plus tard" : masque pendant 7 jours.
  // Initialisation lazy via useState(() => ...) pour ne lire localStorage qu'une fois
  // au mount. Number.isFinite filtre proprement le junk (NaN, '', null, etc.).
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      const until = Number(localStorage.getItem(GMB_BANNER_DISMISS_KEY));
      return Number.isFinite(until) && Date.now() < until;
    } catch (_) {
      return false;
    }
  });
  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(
        GMB_BANNER_DISMISS_KEY,
        String(Date.now() + GMB_BANNER_DISMISS_TTL_MS)
      );
    } catch (_) {}
    setBannerDismissed(true);
  }, []);

  if (loading) return <LoadingSkeleton />;

  // Token expiré ou absent : redirect vers /login avec force=1 pour
  // clear le token expiré localement. Couvre le cas "token expire
  // pendant l'utilisation" qui sortait sur écran blanc avant.
  if (error === 'NO_TOKEN') {
    if (typeof window !== 'undefined') {
      window.location.href = '/login?force=1';
    }
    return <LoadingSkeleton />;
  }

  if (error) return <ErrorState error={error} />;

  return (
    <div style={{ minHeight: '100dvh', background: '#EDEAE3' }}>
      <MobileHeader
        prenom={user?.prenom}
        restaurant={restaurant}
        onSelectRestaurant={openSwitcher}
      />

      <div style={styles.content}>
        {!googleConnected && !bannerDismissed && (
          <GoogleBusinessBanner onConnect={startGmbOAuth} onDismiss={dismissBanner} />
        )}
        <KPIGrid restaurant={restaurant} stats={stats} />
        <ObjectifSection
          avis_generes_revyo={avis_generes_revyo}
          objectif_avis={objectif_avis}
          progression_pilote={progression_pilote}
        />
        <ImpactBusinessSection stats={stats} restaurant={restaurant} user={user} />
        <EvolutionNoteSection restaurant={restaurant} />
      </div>
    </div>
  );
}

const styles = {
  content: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingBottom: 24,
  },
};

function LoadingSkeleton() {
  return (
    <div style={{ minHeight: '100dvh', background: '#EDEAE3', padding: 16 }}>
      <div style={{ height: 60, background: '#E5E5E0', borderRadius: 16, marginBottom: 16, animation: 'revyoPulse 1.5s ease-in-out infinite' }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ height: 100, background: '#E5E5E0', borderRadius: 16, animation: 'revyoPulse 1.5s ease-in-out infinite' }} />
        ))}
      </div>
      <div style={{ height: 220, background: '#E5E5E0', borderRadius: 16, marginBottom: 16, animation: 'revyoPulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 180, background: '#E5E5E0', borderRadius: 16, animation: 'revyoPulse 1.5s ease-in-out infinite' }} />
      <style>{`
        @keyframes revyoPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

function ErrorState({ error }) {
  return (
    <div style={{ minHeight: '100dvh', background: '#EDEAE3', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#6B6B6B' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <p style={{ fontSize: 15, color: '#1A1A1A', marginBottom: 4 }}>Impossible de charger vos données.</p>
        <p style={{ fontSize: 13, marginBottom: 8 }}>Vérifiez votre connexion internet.</p>
        <p style={{ fontSize: 11, color: '#9A9A9A' }}>{String(error)}</p>
      </div>
    </div>
  );
}
